<template>
	<div
		v-if="isOpen"
	>
		<div
			class="modal modal--open"
			:class="{
				'sm': type === 'modal-sm',
				'md': type === 'modal-md',
				'fullscreen': type === 'fullscreen',
				'fit-parent': type === 'fit-parent',
			}"
			role="dialog"
		>
			<div
				class="modal--content"
				:class="{ 'mobile': isMobileWebApp, 'desktop': !isMobileWebApp, 'fit-parent': type === 'fit-parent' }"
			>
				<div class="modal--header">
					<slot name="header">
						<h3>{{ title }}</h3>
					</slot>
					<!-- close button -->
					<button
						v-if="closeable"
						class="modal--close"
						aria-label="Close"
						@click="cancel"
					>
						<Icon
							icon-name="e-remove"
							size="lg"
						/>
					</button>
				</div>
				<!-- body -->
				<div class="modal--body">
					<slot name="body" />
				</div>
				<div
					v-if="hasFooter"
					class="modal--footer"
					:class="footerClasses"
				>
					<div class="modal--footer-buttons">
						<slot name="footer-buttons" />
					</div>
				</div>
			</div>
		</div>
		<!-- backdrop -->
		<div
			v-if="blocking"
			class="backdrop backdrop--open"
			@click.self="backdropClose"
		/>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import Icon from '@components/Icon';
import escape from '@js/mixins/escape';

export default {
	name:       'Modal',
	components: { Icon },
	mixins:     [escape],
	props:      {
		type: {
			type:      String,
			default:   'modal-sm',
			validator: (value) => {
				return ['modal-sm', 'modal-md', 'fullscreen', 'fit-parent'].includes(value);
			},
		},
		title: {
			type:    String,
			default: null,
		},
		footerClasses: {
			type:    Object,
			default: () => {},
		},
		closeable: {
			type:    Boolean,
			default: true,
		},
		open: {
			type:    Boolean,
			default: true,
		},
		hasFooter: {
			type:    Boolean,
			default: true,
		},
		blocking: {
			type:    Boolean,
			default: true,
		},
		fitParent: {
		  type:    Boolean,
			default: false,
		},
		forceBackdropClose: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			isOpen: true,
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp: 'app/isMobileWebApp',
		}),
	},
	watch: {
		open(val) {
			this.isOpen = val;
		},
	},
	mounted() {
		this.isOpen = this.open;
	},
	methods: {
		close() {
			this.isOpen = false;
			this.$emit('close');
		},
		cancel() {
		  this.$emit('cancel');
		  this.close();
		},
		backdropClose() {
			if (this.closeable || this.forceBackdropClose) {
				this.cancel();
			}
		},
		escapeHandler() {
			this.cancel();
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_fonts';

.modal {
	position: fixed;
	top: 0;
	left: 0;
	z-index: 900001;
	width: 100%;
	height: 100%;
	outline: none;
	pointer-events: none;

	&.fit-parent {
		position: absolute;

		.modal--content {
			height: 100%;
			width: 100%;
			margin: 0;
		}
	}
}

.modal--open {
	display: flex;
}

.modal--content {
	display: flex;
	flex-direction: column;
	position: relative;
	margin: auto;
	background: var(--color-grey-850);
	padding: 0;
	box-shadow: 0 0 4px rgb(var(--color-rgb-black), 15%);
	overflow-x: hidden;
	pointer-events: auto;
	transition: transform 0.3s 50ms,opacity 0.3s 50ms,visibility 0.3s 50ms;
	max-width: 100%;

	body.-t-light & {
		background: var(--color-white);
	}

	&.desktop {
		&:not(.fit-parent) {
			max-height: 80vh;
		}

		.sm & {
			width: 436px;
		}

		.md & {
			width: 700px;
	  		max-height: 80vh;
		}

		border-radius: 4px;
	}

	&.mobile {
		width: 100%;
		position: fixed;
		bottom: -500px;
		animation: slide 0.25s forwards;
		max-height: calc(100vh - 80px);
		border-radius: 16px 16px 0 0;

		@keyframes slide {
			100% { bottom: 0; }
		}
	}

	.fullscreen & {
		height: 100%;
		max-height: initial;
	}
}

.modal--header {
	border-bottom: 1px solid var(--color-grey-800);
	display: flex;
	justify-content: space-between;
	align-items: center;

	h3 {
		@include text_md_2(true);
	}

	.desktop & {
		padding: 20px 24px;
	}

	.mobile & {
		padding: 24px 16px;
	}
}

.modal--body {
	@include text-md-1;
	scrollbar-width: none; /* Firefox */
	-ms-overflow-style: none; /* IE 10+ */
	padding: 24px 24px 0;
	color: var(--color-soft-white);
	flex-grow: 1;
	overflow-y: auto;

	body.-t-light & {
		color: var(--color-black);
	}

	.mobile & {
		padding: 20px 16px;
	}
}

.modal--footer {
	padding: 24px 24px;

	.mobile & {
		padding: 24px 16px 32px;
	}

	&-buttons {
		display: flex;
		justify-content: flex-end;

		> * {
			flex: 1 1 100%;

			&:not(:last-child) {
				margin-right: 16px;
			}
		}
	}

  &.vertical {
		.modal--footer-buttons {
			display: flex;
			justify-content: flex-start;
			flex-wrap: wrap;

			> * {
				flex: 1 0 100%;

				&:not(:last-child) {
					margin-bottom: 16px;
				}
			}
		}
  }
}

</style>
